// 面接中・面接直後の画面で、ブラウザ（特にスマホ）の戻る操作で医師の説明画面へ
// 戻ってしまうのを防ぐ。同じ URL の履歴を積み、戻る操作を受け止めて画面に留まる。
//
// Chrome はユーザー操作なしに積まれた履歴があると、同じ document（SPA 全体）の履歴を
// すべて戻るボタンで飛ばす。そのため履歴を積むのはユーザー操作の直後だけにする。
// 戻る操作を受け止めた後は積み直せないので、操作のたびに GUARD_DEPTH 回分を積んでおく。

const GUARD_STATE_KEY = "dentalAvatarBackGuard";
export const INTERVIEW_FINISHED_STATE_KEY = "dentalAvatarInterviewFinished";
export const GUARD_DEPTH = 3;
const ARM_EVENTS = ["click", "pointerup", "keydown"] as const;

type GuardWindow = Pick<Window, "history" | "location" | "addEventListener" | "removeEventListener">;

function readState(state: unknown): Record<string, unknown> {
  return state && typeof state === "object" ? (state as Record<string, unknown>) : {};
}

function readGuardDepth(state: unknown): number {
  const depth = readState(state)[GUARD_STATE_KEY];
  return typeof depth === "number" && Number.isInteger(depth) && depth > 0 ? depth : 0;
}

export function isInterviewFinishedHistoryState(state: unknown): boolean {
  return readState(state)[INTERVIEW_FINISHED_STATE_KEY] === true;
}

export function installBrowserBackGuard(
  win: GuardWindow,
  onBack: () => void,
  { hasUserActivation = false }: { hasUserActivation?: boolean } = {},
): () => void {
  const guardedPathname = win.location.pathname;
  let depth = readGuardDepth(win.history.state);

  const arm = () => {
    if (win.location.pathname !== guardedPathname) return;
    const url = win.location.pathname + win.location.search + win.location.hash;
    while (depth < GUARD_DEPTH) {
      depth += 1;
      win.history.pushState({ ...readState(win.history.state), [GUARD_STATE_KEY]: depth }, "", url);
    }
  };

  // 前の画面のボタン操作で遷移してきた直後は、まだユーザー操作の扱いになっている。
  if (hasUserActivation) {
    arm();
  }

  const handlePopState = (event: PopStateEvent) => {
    // 別画面への遷移（replaceState 後の擬似 popstate など）では何もしない。
    if (win.location.pathname !== guardedPathname) return;
    const nextDepth = readGuardDepth(event.state);
    const wentBack = nextDepth < depth;
    depth = nextDepth;
    if (wentBack) onBack();
  };

  win.addEventListener("popstate", handlePopState);
  ARM_EVENTS.forEach((type) => win.addEventListener(type, arm, true));
  return () => {
    win.removeEventListener("popstate", handlePopState);
    ARM_EVENTS.forEach((type) => win.removeEventListener(type, arm, true));
  };
}
